'use client'

import type { SxProps } from '@mui/material'
import Box from '@mui/material/Box'
import Text from '@mui/material/Typography'
import Image, { StaticImageData } from 'next/image'
import { useRouter } from 'next/navigation'
import { FC, ReactNode } from 'react'

import LuneButton from '@/components/LuneButton'
import { RenderOn } from '@/utills/DeviceUtils'

export interface LearnMoreTileProps {
    icon: string | StaticImageData
    title: ReactNode
    description?: string
    scrollToSection?: string
    href?: string
    renderOnSx?: SxProps
}

const LearnMoreTile: FC<LearnMoreTileProps> = ({
    description,
    title,
    icon,
    scrollToSection,
    href,
    renderOnSx,
}) => {
    const router = useRouter()
    const hasLink = scrollToSection !== undefined || href !== undefined
    const scrollTo = () => {
        if (scrollToSection) {
            const yOffset = -120
            const section = document.getElementById(scrollToSection)
            if (section) {
                const scrollPosition =
                    section.getBoundingClientRect().top + window.pageYOffset + yOffset
                window.scrollTo({ top: scrollPosition, behavior: 'smooth' })
            }
        }
    }

    const Buttons = () => {
        return (
            <>
                {hasLink && (
                    <LuneButton
                        label={'Learn more'}
                        href={href}
                        sx={{
                            position: 'absolute',
                            left: 0,
                            bottom: 0,
                        }}
                    />
                )}
            </>
        )
    }

    const TileContent = () => {
        return (
            <Box
                onClick={() => {
                    if (href) {
                        router.push(href)
                        return
                    }
                    scrollTo()
                }}
                sx={{
                    position: 'relative',
                    height: 'auto',
                    pb: 5,
                    img: {
                        marginLeft: '-12px',
                        marginTop: '-12px',
                    },
                    cursor: hasLink ? 'pointer' : 'normal',
                    '&:hover, &:focus, &:focus-within': {
                        '.MuiSvgIcon-root': {
                            transform: 'translateX(4px)',
                        },
                    },
                }}
            >
                <Image width={72} height={72} src={icon} alt={`${title}-icon`} />
                <Text variant={'h6'} sx={{ mb: 2, mt: 3 }}>
                    {title}
                </Text>
                {description && (
                    <Text variant={'body2'} sx={{ mb: hasLink ? 3 : 0 }}>
                        {description}
                    </Text>
                )}
                <Buttons />
            </Box>
        )
    }

    return (
        <>
            <RenderOn
                breakPoints={['xs', 'sm']}
                sx={{
                    flex: '0 0 calc(50% - 16px)',
                    ...(renderOnSx ?? {}),
                }}
            >
                <TileContent />
            </RenderOn>
            <RenderOn
                breakPoints={['md', 'lg', 'xl']}
                sx={{
                    flex: '0 0 calc(25% - 16px)',
                    ...(renderOnSx ?? {}),
                }}
            >
                <TileContent />
            </RenderOn>
        </>
    )
}

const LearnMoreTiles: FC<{ tiles: LearnMoreTileProps[]; sx?: SxProps; renderOnSx?: SxProps }> = ({
    tiles,
    sx,
    renderOnSx,
}) => {
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
                alignItems: 'stretch',
                ...(sx ?? {}),
            }}
        >
            {tiles.map((tile, index) => (
                <LearnMoreTile
                    key={index}
                    icon={tile.icon}
                    title={tile.title}
                    description={tile.description}
                    scrollToSection={tile.scrollToSection}
                    href={tile.href}
                    renderOnSx={renderOnSx}
                />
            ))}
        </Box>
    )
}

export default LearnMoreTiles
